import {
  ImageOff,
  Loader2,
  Minus,
  Plus,
  ShoppingBag,
  ShoppingCart,
  Trash2,
} from 'lucide-react';
import { RetailTemplate } from '../../domain/Domain';
import RetailDiscountCode from './RetailDiscountCode';

const RetailCart = ({
  items = [],
  totalAmount = 0,
  currencySymbol = '',
  onRemove,
  onRemoveItem,
  onQuantityChange,
  onCheckout,
  onEmptyCart,
  userDetail,
  isLoading,
  isProcessing,
  discountCode,
  appliedDiscount,
  discountAmount = 0,
  checkoutTotal,
  discountError,
  bulkOrderEnabled = false,
  isApplyingDiscount,
  onDiscountCodeChange,
  onApplyDiscount,
  onRemoveDiscount
}) => {
  const cartItems = Array.isArray(items) ? items : [];
  const cartTotal = Number(totalAmount || 0);
  const finalTotal = Number.isFinite(Number(checkoutTotal)) ? Number(checkoutTotal) : cartTotal;
  const removeItem = onRemove || onRemoveItem || (() => {});
  const changeQuantity = onQuantityChange || (() => {});
  const emptyCart = onEmptyCart || (() => {});

  const getProductImage = (productData) => {
    return productData.imageURL ||
      productData.imageContents?.[0]?.displayLink ||
      productData.imageContents?.[0]?.storedLink ||
      '';
  };

  return (
    <RetailTemplate>
      <section className="px-4 py-8">
        <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <h1 className="flex items-center gap-2 text-3xl font-bold text-[#8b0000]">
            {userDetail?.firstName + "'s" || "Your"} Shopping Cart
            <ShoppingBag size={30} />
          </h1>
          <button
            type="button"
            className="inline-flex items-center gap-2 self-start rounded-lg px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50 sm:self-auto"
            onClick={emptyCart}
          >
            <Trash2 size={18} />
            Empty Cart
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center gap-3 py-12 text-gray-700">
            <Loader2 size={22} className="animate-spin text-[#8b0000]" />
            <span>Loading your cart...</span>
          </div>
        ) : cartItems.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="md:col-span-2">
              {cartItems.map((item, index) => {
                const productData = item.product || item.scheduledProduct?.product || {};
                const productId = productData.primaryId;
                const priceAmt = Number(productData.price?.priceAmt ?? 0);
                const quantity = Number(item.quantity || 1);
                const productImage = getProductImage(productData);
                const description = productData.description
                  ? `${productData.description.substring(0, 100)}${productData.description.length > 100 ? '...' : ''}`
                  : '';

                return (
                  <article
                    key={`${productId || 'cart-item'}-${index}`}
                    className="mb-4 rounded-lg border border-gray-200 border-l-[#daa520] bg-white p-4 shadow-sm"
                    style={{ borderLeftWidth: '4px' }}
                  >
                    <div className="flex flex-col gap-4 sm:flex-row">
                      <div className="flex h-[120px] w-full shrink-0 items-center justify-center overflow-hidden rounded-md bg-gray-100 sm:w-[120px]">
                        {productImage ? (
                          <img
                            src={productImage}
                            alt={productData.name}
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <div className="flex flex-col items-center gap-1 text-xs text-gray-500">
                            <ImageOff size={24} />
                            No Image
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <h2 className="text-lg font-bold text-gray-900">
                          {productData.name}
                        </h2>
                        {description && (
  <p className="mb-3 text-sm text-gray-600">
    {description.replace(/<[^>]*>/g, '')}
  </p>
)}

                        <div className="mt-4 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => changeQuantity(productId, quantity - 1)}
                              disabled={quantity <= 1}
                              aria-label="Decrease quantity"
                            >
                              <Minus size={16} />
                            </button>
                            <span className="min-w-8 text-center font-semibold">{quantity}</span>
                            <button
                              type="button"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => changeQuantity(productId, quantity + 1)}
                              disabled={quantity >= 10}
                              aria-label="Increase quantity"
                            >
                              <Plus size={16} />
                            </button>
                          </div>

                          <div className="text-left sm:text-right">
                            <div className="text-lg font-bold text-gray-900">
                              {currencySymbol}{(priceAmt * quantity).toFixed(2)}
                            </div>
                            <div className="text-sm text-gray-600">
                              {currencySymbol}{priceAmt.toFixed(2)} each
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        className="rounded-md px-3 py-1.5 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                        onClick={() => removeItem(productId)}
                      >
                        Remove
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>

            <aside className="md:col-span-1">
              <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-md">
                <h2 className="mb-4 text-center text-2xl font-semibold text-gray-900">
                  Order Summary
                </h2>
                <div className="my-4 border-t border-gray-200" />
                <div className="mb-4 space-y-3">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>{currencySymbol} {cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Shipping</span>
                    <span>{currencySymbol} 0.00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tax</span>
                    <span>{currencySymbol} 0.00</span>
                  </div>
                </div>
                <RetailDiscountCode
                  appliedDiscount={appliedDiscount}
                  currencySymbol={currencySymbol}
                  discountAmount={discountAmount}
                  discountCode={discountCode}
                  error={discountError}
                  isApplying={isApplyingDiscount}
                  isDisabled={isProcessing}
                  onApply={onApplyDiscount}
                  onChange={onDiscountCodeChange}
                  onRemove={onRemoveDiscount}
                />
                {discountAmount > 0 && (
                  <div className="mb-3 flex justify-between font-semibold text-emerald-700">
                    <span>Discount</span>
                    <span>-{currencySymbol} {Number(discountAmount).toFixed(2)}</span>
                  </div>
                )}
                <div className="my-4 border-t border-gray-200" />
                {bulkOrderEnabled && (
                  <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    No payment is required now. Once the minimum requirement is met and the admin approves the order, you will receive an invoice with a payment link.
                  </div>
                )}
                <div className="mb-4 flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span>{currencySymbol} {finalTotal.toFixed(2)}</span>
                </div>
                <button
                  type="button"
                  className="w-full rounded-lg bg-[#8b0000] px-5 py-3 font-bold text-white transition hover:bg-[#6d0000]"
                  onClick={onCheckout}
                  disabled={isProcessing || isApplyingDiscount}
                >
                  {isProcessing ? 'Processing...' : bulkOrderEnabled ? 'Reserve Bulk Booking' : 'Proceed to Checkout'}
                </button>
              </div>
            </aside>
          </div>
        ) : (
          <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
            <ShoppingCart size={52} className="mb-4 text-[#8b0000]" />
            <h2 className="mb-2 text-2xl font-semibold text-gray-900">
              Your cart is empty
            </h2>
            <p className="mb-6 text-gray-600">
              Continue shopping to add items to your cart
            </p>
            <button
              type="button"
              className="rounded-lg bg-[#8b0000] px-8 py-3 font-semibold text-white transition hover:bg-[#6d0000]"
              onClick={() => window.history.back()}
            >
              Go Back
            </button>
          </div>
        )}
      </section>
    </RetailTemplate>
  );
};

export default RetailCart;
